<template>
  <popup-container
    v-if="!isHidden"
    v-model="showPopup"
    :loading="loader.on"
    :close-on-click-out="pendingChangeLogs"
    :title="$t('ChangeLogViewer.title')"
    hide-footer
  >
    <div class="viewerContainer">
      <html-viewer :content="changeLogs" overflow-auto />

      <div class="controller">
        <v-btn @click="ignoreChangeLogs" :disabled="loader.on" color="primary" large>
          <span>{{ $t("ChangeLogViewer.button") }}</span>
        </v-btn>
      </div>
    </div>
  </popup-container>
</template>

<script lang="ts">
import { computed, watch, defineComponent, ref } from "@vue/composition-api"
import { useStore } from "@/composable/useStore"
import { eventBus } from "@/main"
import { PlatformViewType } from "@/types/core"
import { OrganizationType } from "@/types/organization"
import PopupContainer from "@/components/popup/PopupRoot.vue"
import HtmlViewer from "@/components/htmlEditor/HtmlViewer.vue"
import Loader from "@/utils/loader"
import router from "@/router"
import "./scss/_changeLogViewer.scss"

export default defineComponent({
  name: "ChangeLogViewer",
  setup() {
    const { usersModule, organizationModule } = useStore()
    const _showPopup = ref(false)
    const loader = Loader()

    const showPopup = computed({
      get() {
        return _showPopup.value && canShowChangeLog.value
      },
      set(value: boolean) {
        _showPopup.value = value
      }
    })

    const userCanSeeChangeLog = computed(() => {
      const hasSuperRole = usersModule.hasSupportPermissions
      return organizationModule.organizationType === OrganizationType.CustomerHosted || hasSuperRole
    })

    const changeLogs = computed(() => {
      return usersModule.changeLog
    })

    const isHidden = computed(() => {
      const platformTypeIs = localStorage.getItem("view-settings") || PlatformViewType.VIZ_NOW

      if (platformTypeIs === PlatformViewType.APP_LAUNCHER) {
        return true
      }

      return router.currentRoute.meta?.hideFooter === true
    })

    const pendingChangeLogs = computed(() => {
      return !usersModule.pendingChangeLogs
    })

    const canShowChangeLog = computed(() => {
      return !usersModule.pendingAcceptPrivacyPolicy
    })

    const ignoreChangeLogs = () => {
      loader.run(async () => {
        await usersModule.changeLogViewed()
        await usersModule.getMyDetails("forced")
        showPopup.value = false
      })
    }

    loader.run(async () => {
      if (!isHidden.value && userCanSeeChangeLog.value) {
        await usersModule.getMyDetails()
        await usersModule.getChangeLogs()

        showPopup.value = usersModule.pendingChangeLogs
      }
    })

    watch(userCanSeeChangeLog, () => {
      if (userCanSeeChangeLog.value && !isHidden.value) {
        loader.run(async () => {
          await usersModule.getMyDetails()
          await usersModule.getChangeLogs()

          showPopup.value = !usersModule.selfDetail.changeLogViewed
        })
      }
    })

    eventBus.$on("open:changeLog", () => {
      if (!isHidden.value && userCanSeeChangeLog.value) {
        loader.run(async () => {
          await usersModule.getChangeLogs()
          showPopup.value = true
        })
      }
    })

    return {
      isHidden,
      pendingChangeLogs,
      canShowChangeLog,
      ignoreChangeLogs,
      changeLogs,
      loader,
      showPopup
    }
  },
  components: {
    HtmlViewer,
    PopupContainer
  }
})
</script>
